import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Receipt, AlertTriangle, GitBranch, Banknote, Store, FileCheck,
  ScrollText, History, Settings, Users, UserCog, ShieldCheck, LogOut, ChevronLeft, ChevronRight,
  Menu, X, LayoutGrid, CreditCard, Wallet, TrendingDown, Ticket, BarChart3, Coins, ShieldAlert,
  FileBarChart, Bell, UserCircle, Gift, Trophy, Mails, Contact,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

// key, label, icon, superOnly
const ALL_ITEMS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "customers", label: "Customers", icon: Users },
  { key: "merchants", label: "Merchants", icon: Store },
  { key: "kyc", label: "KYC Review", icon: FileCheck },
  { key: "payments", label: "Payments", icon: CreditCard },
  { key: "budget", label: "Central Budget", icon: Wallet },
  { key: "wallets", label: "Customer Wallets", icon: Coins },
  { key: "redemptions", label: "Redemptions", icon: Banknote },
  { key: "tokens", label: "Tokens", icon: Ticket },
  { key: "transactions", label: "Cashback Txns", icon: Receipt },
  { key: "cashback", label: "Cashback Analytics", icon: BarChart3 },
  { key: "loss", label: "Loss & Exposure", icon: TrendingDown },
  { key: "fraud", label: "Fraud & Risk", icon: ShieldAlert },
  { key: "reports", label: "Reports", icon: FileBarChart },
  { key: "referrals", label: "Referrals", icon: Gift },
  { key: "draws", label: "Lucky Draws", icon: Trophy },
  { key: "notifications", label: "Notifications", icon: Bell },
  { key: "messages", label: "Contact Messages", icon: Mails },
  { key: "review", label: "Review Queue", icon: AlertTriangle },
  { key: "campaign", label: "Campaign L1–L20", icon: GitBranch },
  { key: "ledger", label: "Budget Ledger", icon: ScrollText },
  { key: "audit", label: "Audit Logs", icon: History },
  { key: "profile", label: "Admin Profile", icon: UserCircle },
  { key: "plans", label: "Subscription Plans", icon: CreditCard, superOnly: true },
  { key: "config", label: "System Config", icon: Settings, superOnly: true },
  { key: "sitesettings", label: "Contact & Social", icon: Contact, superOnly: true },
  { key: "tree", label: "L1–L20 Tree", icon: GitBranch, superOnly: true },
  { key: "roles", label: "Users & Roles", icon: UserCog, superOnly: true },
];

const TITLES = Object.fromEntries(ALL_ITEMS.map((i) => [i.key, i.label]));

// mobile bottom-nav primary items
const BOTTOM = ["dashboard", "merchants", "redemptions", "review"];

export default function AdminLayout({ active, onNav, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const isSuper = user?.role === "super_admin";
  const items = ALL_ITEMS.filter((it) => !it.superOnly || isSuper);
  const doLogout = async () => { await logout(); navigate("/"); };
  const nav = (k) => { setMoreOpen(false); onNav(k); };

  const bottomItems = items.filter((it) => BOTTOM.includes(it.key));
  const moreItems = items.filter((it) => !BOTTOM.includes(it.key));

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="glass sticky top-0 z-40 border-b border-slate-800">
        <div className="relative flex items-center justify-between px-4 py-3 md:px-6">
          <div className="flex items-center gap-2.5">
            <button data-testid="admin-header-hamburger" onClick={() => setMoreOpen(true)} aria-label="Menu" className="rounded-lg p-1.5 text-slate-200 md:hidden"><Menu className="h-6 w-6" /></button>
            <img src="/paras-logo.png" alt="Paras CashBack" className="h-9 w-9 object-contain" />
            <div className="leading-none">
              <p className="font-heading text-sm font-bold">Paras CashBack</p>
              <p className="mt-0.5 flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-emerald-400">
                <ShieldCheck className="h-3 w-3" /> {isSuper ? "Super Admin" : "Admin"} · {TITLES[active] || "Dashboard"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-slate-400 sm:inline" data-testid="admin-user-email">{user?.email}</span>
            <button data-testid="admin-header-avatar" className="grid h-9 w-9 place-items-center rounded-full bg-emerald-500 font-heading text-sm font-bold text-slate-950">
              {(user?.name || user?.email || "A").charAt(0).toUpperCase()}
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1400px]">
        <aside data-testid="admin-desktop-sidebar" className={`sticky top-[61px] hidden h-[calc(100vh-61px)] flex-shrink-0 flex-col overflow-y-auto border-r border-slate-800 p-3 transition-all duration-200 md:flex ${collapsed ? "w-16" : "w-60"}`}>
          <nav className="flex-1 space-y-1">
            {items.map((it) => {
              const A = active === it.key;
              const I = it.icon;
              return (
                <button key={it.key} data-testid={`admin-sidebar-${it.key}`} onClick={() => onNav(it.key)} title={it.label}
                  className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition ${A ? "bg-emerald-500/15 text-emerald-400" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
                  <I className="h-5 w-5 flex-shrink-0" />
                  {!collapsed && <span className="flex-1 text-left">{it.label}</span>}
                </button>
              );
            })}
          </nav>
          <button data-testid="admin-sidebar-logout" onClick={doLogout} className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-slate-400 transition hover:bg-rose-500/10 hover:text-rose-300">
            <LogOut className="h-5 w-5 flex-shrink-0" />{!collapsed && "Logout"}
          </button>
          <button data-testid="admin-sidebar-collapse" onClick={() => setCollapsed((v) => !v)} aria-label="Collapse sidebar" className="mt-1 flex items-center justify-center rounded-xl px-3 py-2 text-slate-500 transition hover:text-white">
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </aside>

        <main className="min-w-0 flex-1 px-4 py-6 pb-28 md:px-8 md:pb-10">{children}</main>
      </div>

      {/* mobile bottom nav */}
      <nav data-testid="admin-bottom-nav" className="glass fixed inset-x-0 bottom-0 z-50 border-t border-slate-800 pb-[env(safe-area-inset-bottom)] md:hidden">
        <div className="mx-auto grid max-w-md grid-cols-5 items-end">
          {bottomItems.map((it) => {
            const A = active === it.key; const I = it.icon;
            return (
              <button key={it.key} data-testid={`admin-bottomnav-${it.key}`} onClick={() => onNav(it.key)} className={`flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium ${A ? "text-emerald-400" : "text-slate-500"}`}>
                <I className="h-5 w-5" />{it.label.split(" ")[0]}
              </button>
            );
          })}
          <button data-testid="admin-bottomnav-more" onClick={() => setMoreOpen(true)} className={`flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium ${moreOpen ? "text-emerald-400" : "text-slate-500"}`}>
            <LayoutGrid className="h-5 w-5" />More
          </button>
        </div>
      </nav>

      {/* More bottom sheet */}
      {moreOpen && (
        <div className="fixed inset-0 z-[60] md:hidden" data-testid="admin-more-sheet">
          <div className="absolute inset-0 bg-slate-950/70" onClick={() => setMoreOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-3xl border-t border-slate-800 bg-slate-900 p-5 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
            <div className="mb-4 flex items-center justify-between">
              <p className="font-heading text-lg font-bold">All modules</p>
              <button onClick={() => setMoreOpen(false)} aria-label="Close" className="rounded-lg p-1.5 text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {moreItems.map((it) => {
                const I = it.icon;
                return (
                  <button key={it.key} data-testid={`admin-more-${it.key}`} onClick={() => nav(it.key)} className="flex items-center gap-3 rounded-2xl border border-slate-800 p-4 text-left hover:border-emerald-500/40">
                    <I className="h-5 w-5 text-emerald-400" />
                    <span className="text-sm font-semibold">{it.label}</span>
                  </button>
                );
              })}
            </div>
            <button data-testid="admin-more-logout" onClick={doLogout} className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl border border-rose-500/40 p-3 text-sm font-semibold text-rose-300 hover:bg-rose-500/10">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
